import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';

/**
 * How much of a release a teacher chose to share. Each level is a strict superset of the one before it,
 * so this is one dropdown rather than independent field toggles:
 *  - `grade_only`         — grade / percentage / totals, no per-task breakdown at all.
 *  - `points_per_task`    — + per-task label, description, points/maxPoints.
 *  - `points_and_answer`  — + the student's own extracted answer ("Text").
 *  - `full`               — + the AI's grading explanation ("Bewertung").
 *
 * Enforced at WRITE time in `releaseExamResultsMutation` — a `grade_only` release stores `tasks: []`, and
 * `points_per_task`/`points_and_answer` store their `ReleasedTaskResult` rows with the fields above this
 * level's line left `null`. Nothing here is a rendering convention the client is merely told to respect;
 * a query run directly against a release cannot get more than its `detailLevel` promises.
 */
export type ExamResultDetailLevel = 'grade_only' | 'points_per_task' | 'points_and_answer' | 'full';

export const EXAM_RESULT_DETAIL_LEVELS: readonly ExamResultDetailLevel[] = [
  'grade_only',
  'points_per_task',
  'points_and_answer',
  'full',
];

/** One task's result as the student is shown it. A frozen copy — see `ExamResultRelease`. */
@Entity()
export class ReleasedTaskResult extends AdornisEntity {
  static override _class = 'ReleasedTaskResult';

  /** Identity for the client cache, which refuses to merge entity arrays it cannot tell apart.
   * `createSubmissionTaskRows` emits one row per task path, so the label is unique within a release. */
  static override resolveID(data: any) {
    return data?.label;
  }

  /** Display label as the report builds it ("Aufgabe 1", "1.a") — already resolved, not re-derived. */
  @Field(() => String)
  label!: string;

  /** The task text, markdown + LaTeX as authored. Rendered with MarkdownWithMath, never as raw HTML. */
  @Field(() => String)
  description: Maybe<string>;

  /** The student's own answer, as extracted from their sheet. */
  @Field(() => String)
  studentAnswer: Maybe<string>;

  @Field(() => Float)
  points!: number;

  @Field(() => Float)
  maxPoints!: number;

  /** Why those points — the grading explanation. NOT the grading scheme, which stays teacher-internal. */
  @Field(() => String)
  feedback: Maybe<string>;
}

/**
 * One student's released result for one exam — a FROZEN SNAPSHOT, not a view onto the live submission.
 *
 * This record exists for two reasons, and both matter:
 *
 * 1. **It is the snapshot.** A teacher who re-grades after distributing has not changed what their students
 *    were told; the result they see is the one that was released, until the teacher releases again.
 *
 * 2. **It is the whole student read path.** Because the payload lives here, keyed by `studentID`, a student
 *    reading their own result never touches `Exam` or `Submission` — both of which belong to the TEACHER and
 *    are denied to students by their own ownership checks (src/db/authz). No raw-collection bypass, no name
 *    matching at read time, no access to another student's row: one exact lookup on their own id.
 *
 * Created only by `releaseExamResultsMutation`, after a teacher has confirmed which submission belongs to
 * which student. Deleting the record revokes access; the `Submission.studentID` binding survives it, because
 * whose paper it is does not stop being true when the result is withdrawn.
 */
@Entity()
export class ExamResultRelease extends MongoEntity {
  static override _class = 'ExamResultRelease';
  static override _collectionName = 'examResultReleases';

  @Field(() => String)
  examId!: string;

  /** Denormalized so the student's per-class list is one query, without joining back through Exam. */
  @Field(() => String)
  classRoomID!: string;

  /** FK to `Student._id` — the only thing that decides who may read this record. */
  @Field(() => String)
  studentID!: string;

  /** Provenance: which submission this snapshot was taken from. */
  @Field(() => String)
  submissionId!: string;

  @Field(() => String)
  examTitle: Maybe<string>;

  @Field(() => String)
  examSubject: Maybe<string>;

  /** Grade label in the exam's own scale ("2+", "A", "13") — null when the scale yields none. */
  @Field(() => String)
  grade: Maybe<string>;

  @Field(() => Float)
  percentage: Maybe<number>;

  @Field(() => Float)
  totalPoints: Maybe<number>;

  @Field(() => Float)
  totalMaxPoints: Maybe<number>;

  @Field(() => [ReleasedTaskResult])
  tasks: Maybe<ReleasedTaskResult[]>;

  /** What was actually shared — see `ExamResultDetailLevel`. Safe to always show in full: this describes
   * the release itself, not the exam content it does or doesn't carry. */
  @Field(() => String)
  detailLevel!: string;

  /**
   * The submission's own scanned pages / submitted PDF, attached only when the teacher checked "Eigene
   * Abgabe mitverteilen" at release time — absent otherwise, the same fail-closed rule as `detailLevel`.
   * Populated from `Submission.imageIds` (scanned) or `Submission.submissionPdfId` (online); a scanned
   * submission has no PDF of its own, so `attachedPdfId` is null there even when the checkbox was set.
   *
   * SECURITY NOTE, worth being explicit about: `filesRouter`'s serve/download route
   * (@adornis/file-utils) reads these ids straight off `getRawCollection` with no session or ownership
   * check at all — an id is a bearer token, not a scoped grant. That is a pre-existing, framework-wide
   * property of every file in this app (blank-exam pages, uploaded PDFs, …), not something this release
   * mechanism introduces or could tighten on its own; the only thing this record controls is WHOM it
   * hands the id to, via the same studentID-scoped read as everything else in this entity.
   */
  @Field(() => [String])
  attachedImageIds: Maybe<string[]>;

  @Field(() => String)
  attachedPdfId: Maybe<string>;

  @Field(() => String)
  releasedAt!: string;

  /** The `Teacher._id` that released it — who published this, for the audit trail. */
  @Field(() => String)
  releasedBy!: string;
}
